/*
 * This script manipulates the DOM structure of the customer portal subscription page and its additional functionalities.
 *
 * Note:
 * The script uses the setTimeout function to wait until the React element from the Recharge app is available. This is applied in the following functions:
 * - waitForReactComponentLoad()
 * - handleNextOrderClick()
 * - handleUpcomingOrderClick()
 * - handleOrdersPage()
 * - handleSubscriptionsPage()
 * - handleCustomerPage()
 * - initiateOrderSubscriptionList()
 * - initiateSubscriptionList()
 * - handleSubscriptionITEMPage()
 * - disableButton()
 *
 * @example
 *  function handleNextOrderClick() {
 *    const dynamicElement = document.querySelector('#recharge-customer-portal .recharge-container .recharge-section-next-order-actions');
 *    if (dynamicElement) {
 *      handleOverviewPage();
 *    } else {
 *      setTimeout(handleNextOrderClick, 100);
 *    }
 *  }
 *  dynamicElement is the unique element from the React component for that particular page.
 */


// global variables to access across functions
let customerZipCode, nextOrderDate, deliveryDateData, rates, currentURL,  currentPage, nextOrderSectionBTN, upcomingOrderSectionBTN, dateParentContainer, cutoffDate, deliveryDate, backBtn, orderSkipped;

window.onload = function () {
  waitForReactComponentLoad();
};

/*
 * This function creates a new section-tab button, "View our menu", which redirects to the collections page.
 * It takes the active sectionButton as an input argument, checks whether the new button already exists or not.
 * If it does not exist, it will create a clone of the inactive tab and change the URL to /collections/all and updates the text content.
 * The function uses the .insertBefore DOM method to insert the new button at the top.
 *
 * @param {HTMLButtonElement} button - The button element that is required to identify the active button and serves as a reference.
 */
function addSectionTabContent(button) {
  const buttonExist = document.querySelector('.collections-page-link');
  if (!buttonExist) {
    const sectionTabContainer = button.parentNode;
    const sectionFirstTab = sectionTabContainer.firstChild;
    const sectionReferenceTab = button.nextSibling || button.previousSibling;
    const viewOurMenuTab = sectionReferenceTab.cloneNode(true);
    viewOurMenuTab.classList.add('collections-page-link');
    const viewOurMenuLink = viewOurMenuTab.querySelector('a');
    viewOurMenuLink.href = '/collections/all';
    const menuInnerTextValue = viewOurMenuTab.querySelector('span.recharge-heading');
    menuInnerTextValue.innerHTML = 'View our menu';
    sectionTabContainer.insertBefore(viewOurMenuTab, sectionFirstTab);
  }
}

/*
 * This function waits for React components from the app to load when the page is reloaded.
 * Once loaded, it calls fetchAppData to collect the required information and then invokes initialCheck.
 */
async function waitForReactComponentLoad() {
  const dynamicElement = document.querySelector('#recharge-customer-portal .recharge-container .react-loading-skeleton');
  if (!dynamicElement) {
    await fetchAppData();
    initialCheck();
  } else {
    setTimeout(waitForReactComponentLoad, 100);
  }
}

/*
 * This function is called when the React components are loaded. After the load:
 * 1. The current page is set by calling setCurrentPageFromURL();
 * 2. The zipcode of the customer is fetched by calling getZipCode();
 */
async function fetchAppData() {
  setCurrentPageFromURL();
  customerZipCode = await getZipCode();
}

/*
 * This function initializes all the tab switching buttons and adds event listeners to them.
 * It then calls the function based on the currentPage.
 *
 * Event listeners for the nextOrderSectionBTN and upcomingOrderSectionBTN are not added in this function, because DOM manipulation is required for those triggers.
 * To avoid multiple event trigger, event listeners are added in their respective functions for these two buttons.
 */
function initialCheck() {
  nextOrderSectionBTN = document.querySelector('a[data-testid="recharge-internal-view-your-next-order"]').parentNode;
  upcomingOrderSectionBTN = document.querySelector('a[data-testid="recharge-internal-view-upcoming-orders"]').parentNode;
  previousOrderSectionBTN = document.querySelector('a[data-testid="recharge-internal-view-previous-orders"]').parentNode;
  subscriptionSectionBTN = document.querySelector('a[data-testid="recharge-internal-manage-subscriptions"]').parentNode;
  addressSectionBTN = document.querySelector('a[data-testid="recharge-internal-address-&-payment-details"]').parentNode;

  previousOrderSectionBTN.addEventListener('click', handleOrdersPage);
  subscriptionSectionBTN.addEventListener('click', handleSubscriptionsPage);
  addressSectionBTN.addEventListener('click', handleCustomerPage);

  switch (currentPage) {
  case 'overview':
    handleOverviewPage();
    break;
  case 'overview-subscription':
    handleSubscriptionITEMPage();
    break;
  case 'schedule':
    handleSchedulePage();
    break;
  case 'orders':
    handleOrdersPage();
    break;
  case 'subscriptions':
    handleSubscriptionsPage();
    break;
  case 'subscriptions/':
    handleSubscriptionITEMPage();
    break;
  case 'customer':
    handleCustomerPage();
    break;
  default:
    console.log('url not updated');
    break;
  }
}

/*
 * This function takes a parameter and converts it into the required format accepted by the API and returns the formatted date.
 *
 * @param {string} dateString - A string value representing a 'date' that needs to be converted to the API-acceptable format.
 * @returns {string} - Formatted date in the following format: "2024-01-01T18:00:00+11:00"
 */
function getDateFormatForAPi(dateString) {
  const inputDate = new Date(dateString);

  const year = inputDate.getFullYear();
  const month = (inputDate.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based, so add 1
  const day = inputDate.getDate().toString().padStart(2, '0');
  const hours = '18'; // Assuming a default time of 18:00:00
  const minutes = '00';
  const seconds = '00';
  const timezoneOffset = 11; // UTC+11:00

  const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+${timezoneOffset < 10 ? '0' : ''}${timezoneOffset}:00`;

  return formattedDate;
}

/*
 * This function searches the script with customer json added in the DOM to retrieve customer details and fetches the Zipcode from that customer object, returning the zipcode.
 *
 * @returns {string} - Zipcode of the first available address.
 */
async function getZipCode() {
  let scriptTagDoc = document.querySelector('main > script + script').innerHTML;
  scriptTagDoc = scriptTagDoc.slice(17);
  const customerObject = JSON.parse(scriptTagDoc);
  return customerObject.addresses[0].zip;
}

/*
 * This asynchronous function hits the delivery check API and returns the rates array from the API response.
 * Rates are used to display delivery and dispatch date for upcoming orders to the users.
 *
 * @returns {array} - Rates array, which contains dispatch and delivery date details.
 */
async function getDatesFromAPI() {
  const nextOrderDateContainer = document.querySelector('.recharge-section-next-order-actions .recharge-card > div > div:first-child > div > div:nth-child(2) > div > div:nth-child(2) h1.recharge-heading-h1');
  const orderDateValue = nextOrderDateContainer.innerHTML;
  nextOrderDate = getDateFormatForAPi(orderDateValue);

  try {
    const pinCodeValue = customerZipCode;
    const nextOrderDateValue = nextOrderDate;
    const formData = {
      postal_code: pinCodeValue,
      orderDate: nextOrderDateValue
    };
    const jsonData = JSON.stringify(formData);
    const response = await fetch(
      'https://tortuga-oms--development.gadget.app/rate-old',
      {
        method: 'POST',
        body: jsonData,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    const data = await response.json();
    rates = data.rates;
    return rates;
  } catch (error) {
    console.error('Error:', error);
  }
}

/*
 * This function sets the currentPage value based on the URL when the page is reloaded.
 */
function setCurrentPageFromURL() {
  currentURL = window.location.href;
  if (currentURL.includes('overview')) currentPage = 'overview';
  if (currentURL.includes('overview') && currentURL.includes('&subscription=')) currentPage = 'overview-subscription';
  if (currentURL.includes('schedule')) currentPage = 'schedule';
  if (currentURL.includes('orders')) currentPage = 'orders';
  if (currentURL.includes('subscriptions')) currentPage = 'subscriptions';
  if (currentURL.includes('subscriptions/')) currentPage = 'subscriptions/';
  if (currentURL.includes('customer')) currentPage = 'customer';
}

/*
 * This function handles the overview page or view next order page.
 * It checks whether the order is skipped or not. If not skipped, it will invoke a function to fill all the dates.
 * It handles event listeners for nextOrderSectionBTN and upcomingOrderSectionBTN.
 */
function handleOverviewPage() {
  dateParentContainer = document.querySelector('.recharge-section-next-order-actions .recharge-card > div > div:first-child > div > div:nth-child(2)');
  dateContainer = dateParentContainer.querySelector('div');

  const orderSkippedContainer = document.querySelector('.recharge-section-next-order-actions .recharge-card > div > div:nth-child(2) > div > div > div');
  orderSkipped = orderSkippedContainer.getAttribute('role');

  if (orderSkipped !== 'alert') {
    fillDates();
  }

  const sendNowBtnContainer = document.querySelector('#recharge-customer-portal .recharge-section-next-order-actions button[data-testid="recharge-internal-send-now-button"]').parentNode.parentNode;
  const sendNowBtn = sendNowBtnContainer.querySelector('span.recharge-text');
  sendNowBtnContainer.classList.add('pay-now-button');
  sendNowBtn.innerHTML = 'Pay now';

  nextOrderSectionBTN.removeEventListener('click', handleNextOrderClick);
  upcomingOrderSectionBTN.addEventListener('click', handleUpcomingOrderClick);
  initiateOrderSubscriptionList();

  // addSectionTabContent(nextOrderSectionBTN);
}

/*
 * This function handles the schedule page or view upcoming orders page.
 * It handles event listeners for upcomingOrderSectionBTN.
 */
function handleSchedulePage() {
  // addSectionTabContent(upcomingOrderSectionBTN);
  const heading1 = document.querySelector('.recharge-section-orders-next .recharge-card .recharge-heading.recharge-heading-h1');
  heading1.innerHTML = 'Your payment date';
  const heading2 = document.querySelector('#recharge-customer-portal > div > div > div > div > div > div > div > div > div > div._15b7gxl0._17o99wpif._17o99wpsl._1jrh2c60 > div > div:nth-child(2) > div > div > div > div:nth-child(1) > h2');
  heading2.innerHTML = 'Upcoming payments';
  handleBackBtnClick();
  upcomingOrderSectionBTN.removeEventListener('click', handleUpcomingOrderClick);
}

/*
 * The functions mentioned below handle their respective pages.
 * They wait for the dynamic element to load, and after that, they add an event listener to the button on that page:
 * - handleOrdersPage()
 * - handleSubscriptionsPage()
 * - handleCustomerPage()
 */
function handleOrdersPage() {
  const dynamicElement = document.querySelector('#recharge-customer-portal .recharge-container .recharge-section-orders-previous');
  if (dynamicElement) {
    // addSectionTabContent(nextOrderSectionBTN);
    handleBackBtnClick();
  } else {
    setTimeout(handleOrdersPage, 100);
  }
}
function handleSubscriptionsPage() {
  const dynamicElement = document.querySelector('#recharge-customer-portal .recharge-container .recharge-section-subscriptions');
  if (dynamicElement) {
    // addSectionTabContent(nextOrderSectionBTN);
    addViewOurMenuBtn();
    handleBackBtnClick();
    initiateSubscriptionList();
  } else {
    setTimeout(handleSubscriptionsPage, 100);
  }
}
function handleCustomerPage() {
  const dynamicElement = document.querySelector('#recharge-customer-portal .recharge-container .recharge-section-account-header');
  if (dynamicElement) {
    // addSectionTabContent(nextOrderSectionBTN);
    handleBackBtnClick();
  } else {
    setTimeout(handleCustomerPage, 100);
  }
}

/*
 * This function adds an event listener to the back button to handle the overview page functionalities.
 */
function handleBackBtnClick() {
  nextOrderSectionBTN.addEventListener('click', handleNextOrderClick);
  backBtn = document.querySelector('.recharge-card .recharge-action-link').parentNode;
  backBtn.addEventListener('click', handleNextOrderClick);
}

/*
 * The functions mentioned below handle their respective pages on click of the section tab buttons.
 * - handleNextOrderClick()
 * - handleUpcomingOrderClick()
 */
function handleNextOrderClick() {
  const dynamicElement = document.querySelector('#recharge-customer-portal .recharge-container .recharge-section-next-order-actions');
  if (dynamicElement) {
    handleOverviewPage();
  } else {
    setTimeout(handleNextOrderClick, 100);
  }
}
function handleUpcomingOrderClick() {
  const dynamicElement = document.querySelector('#recharge-customer-portal .recharge-container .recharge-section-orders-next');
  if (dynamicElement) {
    handleSchedulePage();
  } else {
    setTimeout(handleUpcomingOrderClick, 100);
  }
}

/*
 * The functions mentioned below handle their respective pages.
 * They wait for the dynamic element to load, and after that, they add an event listener to the subscription items on that page:
 * - initiateOrderSubscriptionList() - handles the subscription items in the overview page / view your next order page
 * - initiateSubscriptionList() - handles the subscription items in the subscription page / manage subscription page
 */
function initiateOrderSubscriptionList() {
  const dynamicElement = document.querySelector('#recharge-customer-portal .recharge-container [data-testid="line-item-price"]');
  if (dynamicElement) {
    const subscriptionItems = document.querySelectorAll('#recharge-customer-portal .recharge-container [data-testid="recharge-internal-general-view-button"]');
    subscriptionItems.forEach((item)=> {
      item.addEventListener('click', handleSubscriptionITEMPage);
    });
  } else {
    setTimeout(initiateOrderSubscriptionList, 100);
  }
}
function initiateSubscriptionList() {
  const dynamicElement = document.querySelector('#recharge-customer-portal .recharge-container .recharge-component-subscription-item');
  if (dynamicElement) {
    const subscriptionItems = document.querySelectorAll('.recharge-component-subscription-item');
    subscriptionItems.forEach((item) => {
      item.addEventListener('click', handleSubscriptionITEMPage);
    });
  } else {
    setTimeout(initiateSubscriptionList, 100);
  }
}

/*
 * This function waits for the dynamic element to load, and after that, it invokes a function to create and handle the pause button.
 * It passes the cancel button as an argument to the function.
 */
function handleSubscriptionITEMPage() {
  const dynamicElement = document.querySelector('.recharge-component-subscription-cancel');
  if (dynamicElement) {
    handlePauseOption(dynamicElement);
  } else {
    setTimeout(handleSubscriptionITEMPage, 100);
  }
}

/*
 * This function receives a cancel button as input and clones it to create a new button with similar functionality.
 * New button: pause subscription
 * It adds an event listener to the new button to mimic the event of the cancel button.
 *
 * @param {HTMLButtonElement} button - The button element that is required for cloning the new button.
 */
function handlePauseOption(button) {
  const buttonCon = button.firstChild.firstChild;
  const cancelBTN = buttonCon.querySelector('span.recharge-action-link');
  const pauseBTN = cancelBTN.cloneNode(true);
  pauseBTN.classList.add('button-new-appended');
  pauseBTN.style.marginLeft = '2rem';
  buttonCon.appendChild(pauseBTN);

  const pauseBtnText = document.querySelector('.button-new-appended > span > div > div:nth-child(2)');
  pauseBtnText.classList.add('pause-sub');
  pauseBtnText.innerHTML = 'Pause subscription';
  pauseBTN.addEventListener('click', (event) => {
    triggerOriginalElementClick(cancelBTN, event);
  });

  const pauseIcon = document.querySelector('.button-new-appended > span > div > div:first-child > div');
  pauseIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="rgb(29, 29, 31)" viewBox="0 0 256 256"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216ZM112,96v64a8,8,0,0,1-16,0V96a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V96a8,8,0,0,1,16,0Z"></path></svg>';
  const SVGIcon = pauseIcon.querySelector('svg');
  SVGIcon.style.fill = '#1D1D1F';

  const backBTN = document.querySelector('.recharge-action-link');
  backBTN.addEventListener('click', handleSubscriptionsPage);
}
function triggerOriginalElementClick(orgBtn, event) {
  if (orgBtn) {
    orgBtn.click();
    disableButton(event.target);
  }
}

/*
 * This function receives a cancel button as input and waits for the dynamic element to load.
 * Dynamic element: Cancel subscription button in the pop-up of the pause subscription event.
 * Once the pop-up is loaded, the button is visually hidden.
 */
function disableButton(button) {
  const dynamicElement = document.querySelector('#PAUSE_BEFORE_CANCEL .recharge-modal-wrapper');
  if (dynamicElement) {
    const cancelBTN = document.querySelector('#PAUSE_BEFORE_CANCEL form > div > div:nth-child(2) > div > div > div > div:nth-child(2) > div > button');
    cancelBTN.style.display = 'none';
  } else {
    setTimeout(()=>{
      disableButton(button);
    }, 100);
  }
}

/*
 * This function appends the dispatch and delivery date in the overview / view next order page.
 * It creates a clone of the existing date container for dispatch and delivery date.
 * It retrieves the dates from the API, formats the dates, and fills the new dates in the cloned containers.
 */
async function fillDates() {
  await getDatesFromAPI();
  const newRates = rates[0];
  cutoffDate = newRates.cutoff;
  deliveryDate = newRates.delivery;

  cutoffDate = getDateFormat(cutoffDate);
  cutoffDate = convertDateFormat(cutoffDate);
  deliveryDate = getDateFormat(deliveryDate);
  deliveryDate = convertDateFormat(deliveryDate);

  dateParentContainer.style.display = 'flex';

  const clonedDispatchDate = dateContainer.cloneNode(true);
  const dateValue = clonedDispatchDate.querySelector('h1.recharge-heading');
  const dateTitle = clonedDispatchDate.querySelector('span.recharge-text-p3');
  // dateValue.innerHTML = cutoffDate;
  dateTitle.innerHTML = 'Next payment date';
  dateParentContainer.appendChild(clonedDispatchDate);

  // const clonedDeliveryDate = dateContainer.cloneNode(true);
  // clonedDeliveryDate.style.marginLeft = '4rem';
  // dateValue = clonedDeliveryDate.querySelector('h1.recharge-heading'),
  // dateTitle = clonedDeliveryDate.querySelector('span.recharge-text-p3');
  // dateValue.innerHTML = deliveryDate;
  // dateTitle.innerHTML = 'Delivery date';
  // dateParentContainer.appendChild(clonedDeliveryDate);
}

/*
 * This function create new button which redirect to /collections/all#meals-bundles page
 */
function addViewOurMenuBtn() {
  const parentContainer = document.querySelector('.recharge-container .recharge-section-subscriptions > div > div');
  const referenceDiv = document.querySelector('.recharge-text-p3');

  const clonedBTN = referenceDiv.cloneNode(true);
  clonedBTN.classList.add('addSubscriptionBTN');
  parentContainer.appendChild(clonedBTN);

  const addSubscriptionBTNContainer = clonedBTN.querySelector('div');
  addSubscriptionBTNContainer.style.flexDirection = 'row-reverse';
  const contentSVGContainer = addSubscriptionBTNContainer.querySelector('div:first-child svg').parentNode;
  contentSVGContainer.classList.add('clonedSVGContainer');
  contentSVGContainer.innerHTML = '<svg viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg" width="10" height="10"><path d="m1.5 15 7-7-7-7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>'
  contentSVGContainer.parentNode.style.backgroundColor = 'transparent';

  addSubscriptionBTNContainer.addEventListener('click', () => {
    window.location.href = '/collections/all';
  });
  const addSubscriptionText = addSubscriptionBTNContainer.querySelector('div:nth-child(2) .recharge-text');
  addSubscriptionText.innerHTML = 'Start new subscription';
}

/*
 * This function extracts and returns the date portion from a given date string.
 *
 * @param {string} dateString - The input date string from which the date portion is extracted.
 * @returns {string} - The extracted date portion.
 */
function getDateFormat(dateString) {
  let count = 0;
  let date = '';
  for (let i = 0; i < dateString.length; i++) {
    if (dateString[i] === ' ') count++;
    if (count === 3) break;
    date += dateString[i];
  }
  return date;
}
/*
 * This function converts the input date string into a formatted date.
 *
 * @param {string} inputDateString - The input date string to be converted.
 * @returns {string} - The formatted date string. example: "Tue, January 16, 2024"
 */
function convertDateFormat(inputDateString) {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const match = inputDateString.match(/(\w{3}) (\d{1,2})(?:st|nd|rd|th)? (\w+)/);

  if (match) {
    const dayOfWeek = match[1];
    const dayOfMonth = match[2];
    const monthAbbreviationOrName = match[3];
    let monthIndex = months.indexOf(monthAbbreviationOrName);
    if (orderSkipped == 'alert') {
      // dayOfMonth = match[2] + 7;
      console.log('order is skipped');
    }

    if (monthIndex === -1) monthIndex = months.findIndex((month) => month.toLowerCase().startsWith(monthAbbreviationOrName.toLowerCase()));
    if (monthIndex !== -1) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      currentDate.setDate(parseInt(dayOfMonth, 10));
      currentDate.setMonth(monthIndex);
      currentDate.setFullYear(currentYear);

      const formattedDate = currentDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });

      return formattedDate;
    } else {
      console.error('Invalid month abbreviation');
      return inputDateString;
    }
  } else {
    console.error('Invalid date format');
    return inputDateString
  }
}

/* end */
